import RpcSubprovider from 'web3-provider-engine/subproviders/rpc';
import WSSubprovider from 'web3-provider-engine/subproviders/websocket';
import ProviderEngine from 'web3-provider-engine';
import Dec from 'decimal.js';

import { AbstractProvider } from 'web3-core';
import { WalletLinkProvider } from 'walletlink';
import { JSONRPCRequest } from 'walletlink/dist/provider/JSONRPC';
import { IBinanceProvider } from '../../global';
import {networks, INetwork } from '../constants/networks';

/* eslint-disable @typescript-eslint/ban-types */
export function promisify<T = any>(func: Function, args: string[]) {
  /* eslint-enable @typescript-eslint/ban-types */
  return new Promise<T>((res, rej) => {
    func(...args, (err: Error, data: T) => {
      if (err) return rej(err);
      return res(data);
    });
  });
}

export const getAccounts = async (provider: AbstractProvider | WalletLinkProvider | IBinanceProvider) => new Promise<string | undefined>((resolve, reject) => {
  const request: JSONRPCRequest = {
    jsonrpc: '2.0', method: 'eth_accounts', params: [], id: 1,
  };
  provider.sendAsync(request, (err, res) => {
    if (err) {
      reject(err);
    } else {
      resolve(res.result[0]);
    }
  });
});

export const getProviderEngine = () => Promise.resolve(ProviderEngine);
export const getRpcProvider = (rpcUrl: string) => Promise.resolve(rpcUrl.startsWith('http') ? new RpcSubprovider({ rpcUrl }) : new WSSubprovider({ rpcUrl }));

declare global {
  interface Window {
    opera: any;
    MSStream: any;
  }
}

// Checks what operating system is used for mobile devices
// @return {string}
export const getMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone';
  }
  if (/android/i.test(userAgent)) {
    return 'Android';
  }
  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'iOS';
  }
  return 'unknown';
};

export const browserDetect = () => {
  const ua = window.navigator.userAgent.toLowerCase();

  // Detect browsers (only the ones that have some kind of quirk we need to work around)
  if (ua.match(/chrome/gi) !== null) return 'Chrome';
  if (ua.match(/firefox/gi) !== null) return 'Firefox';
  if (ua.match(/brave/gi) !== null) return 'Brave';
  if (ua.match(/edge/gi) !== null) return 'Edge';
  // If any case miss we will return null
  return null;
};

export const wait = (timeout = 500) => new Promise((resolve) => setTimeout(resolve, timeout));

export const numberWithCommas = (x: string, decimals = 8, removeTrailingZeroes = true) => {
  if (!Number.isFinite(parseFloat(x))) return '0';
  if (parseFloat(x).toString() === '0') return '0';

  const parts = new Dec(x).toFixed(decimals).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  let formatted = parts.join('.');
  if (removeTrailingZeroes) {
    formatted = formatted
      .replace(/(\.\d*[1-9])0*$/, '$1') // 123.1200 -> 123.12
      .replace(/\.0*$/, '') // 123.00 -> 123
      .replace(/^0.0+$/, '0'); // 0.00 -> 0
  }
  return formatted;
};

const formatNumberLessThanOne = (x: number, skipScientificNotation: boolean) => {
  let str = (x).toString();
  let res = '';

  // For scientific notation like "1.46972274453454e-7"
  if (str?.length && str[0] !== '0') {
    str = Number(str).toFixed(20);
    if (skipScientificNotation) {
      return '~0';
    }
  }

  // in case of 0.0123 -> 0.012
  for (let i = 0; i < str.length; i += 1) {
    if (+str[i] && str[i] !== '0') {
      res = str.substr(0, i + 1);

      // add the second number
      if (i < str.length && +str[i + 1] && str[i + 1] !== '0') {
        res = res + str[i + 1];
      }

      break;
    }
  }

  // in case of 0
  if (!res) {
    res = str;
  }
  return res;
};

export const formatNumber = (_num: string, decimals?: number, removeTrailingZeroes?: boolean, skipScientificNotation?: boolean) => {
  try {
    if (!Number.isFinite(parseFloat(_num))) return '0';
    const sign = parseFloat(_num) < 0 ? '-' : '';
    const num = Math.abs(parseFloat(_num));
    if (num < 1) return formatNumberLessThanOne(num, skipScientificNotation);
    if (num < 10000) return numberWithCommas(_num, decimals || 2, removeTrailingZeroes);

    const si = [
      { value: 1, symbol: '' },
      { value: 1E3, symbol: 'k' },
      { value: 1E6, symbol: 'M' },
      { value: 1E9, symbol: 'B' },
      { value: 1E12, symbol: 'T' },
      { value: 1E15, symbol: 'P' },
      { value: 1E18, symbol: 'E' },
    ];
    let i;

    for (i = si.length - 1; i > 0; i -= 1) {
      if (num >= si[i].value) {
        break;
      }
    }

    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    return sign + new Dec(num / si[i].value).toFixed(decimals || 2).replace(rx, '$1') + si[i].symbol;
  } catch (e) {
    return _num.toString();
  }
};

export const getNetworkToSwitch = (networkId: number) => {
  const network: INetwork = networks[networkId];
  if (!network) {
    return network;
  }
  Object.keys(network).forEach((key: string) => {
    // @ts-ignore
    const value = network[key];
    if (Array.isArray(value) && !value.length) {
      // @ts-ignore
      delete network[key];
    }
  });

  return network;
}